import type { OperationVariables, WatchQueryOptions } from '@apollo/client';
import {
  type Exact,
  type GetAppStatusRealworksQuery,
  type UpdateRealworksAppStatusMutationVariables,
  useUpdateRealworksAppStatusMutation,
} from '~/graphql/types';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import type { KenmerkLookup } from '../../types';

type UpdateQueryFn = <
  TVars extends OperationVariables = Exact<{ accountId: string }>,
>(
  mapFn: (
    previousQueryResult: GetAppStatusRealworksQuery,
    options: Pick<
      WatchQueryOptions<TVars, GetAppStatusRealworksQuery>,
      'variables'
    >,
  ) => GetAppStatusRealworksQuery,
) => void;

const useSyncMutation = (
  updatedKenmerkLookup: KenmerkLookup | null,
  updateQuery: UpdateQueryFn,
) => {
  const addToast = useAddToast();

  const [updateRealworksAppStatus, { loading: updating }] =
    useUpdateRealworksAppStatusMutation();

  const onUpdate = (
    variables: UpdateRealworksAppStatusMutationVariables,
    { errorToast, successToast }: { errorToast: string; successToast: string },
  ) => {
    if (!updatedKenmerkLookup) return;

    return updateRealworksAppStatus({
      variables,
    }).then(({ errors, data }) => {
      if (errors && errors.length !== 0) {
        addToast([formatToastMessage(errorToast, 'danger')]);
        return;
      }

      if (!data) return;

      updateQuery(prevRes => ({
        ...prevRes,
        getAppStatusRealworks: data.updateRealworksAppStatus,
      }));

      addToast([formatToastMessage(successToast, 'success')]);
    });
  };

  return { onUpdate, updating };
};

export default useSyncMutation;
